import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { TEAM_RECORDS } from "../../constants/routes";
import MultiSelectDropdown from "../MultiSelectDropdown";
import Pill from "../Pill";
import DropdownSelect from "../DropdownSelect";
import Pagination from "../Pagination";
import Spinner from "../Spinner";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community";
import api from "../../utils/api";
import { useParams } from "react-router-dom";
import { useReportIssue } from "../../hooks/useReportIssue";
import { ReportIssueModal } from "../ReportIssueModal";
import { useReactiveRef } from '../../utils/UseReactiveState';
import { AuthContext } from "../../store/ContextStore";

interface TeamData {
  SportCode: string,
  TeamName: string,
  TeamCode: string,
  stat_mapping: {
    [key: string | number]: any;
  }
}

const TeamRecordsView = (props: { TeamData: TeamData | null | undefined }) => {
    const {  sportsCode, teamCode } = useParams();

        const [currentPage, setCurrentPage, currentPageRef] = useReactiveRef<number>(0);
        const [itemsPerPage, setItemsPerPage, itemsPerPageRef] = useReactiveRef<number>(10);
        
        const [errorStatus, setErrorStatus] = useState<number | null>(null);
        const [errorMessage, setErrorMessage] = useState<string | null>(null);

        const [selectedFilters, setSelectedFilters] = useState<{ seasons: number[], substat: string[], stat: string[] }>({ seasons: [], substat: [], stat: [] });
        const [isLoading, setisLoading] = useState(false);
        const [currentDuration, setCurrentDuration] = useState<"season" | "game">("game")

        const [teamRecordsRes, setTeamRecordsRes, teamRecordsResRef] = useReactiveRef<TeamRecordsRes | null>(null);


          const formatNumericValue = (value: any): string | number => {
            // Handle "-" string explicitly FIRST
            if (value === "-" || value === null || value === undefined) return '-';
            
            const stringValue = String(value).trim();
            if (stringValue === "-") return '-';
            
            // If it's already a number type, format it
            if (typeof value === 'number') {
            if (Number.isInteger(value)) {
                return value;
            }
            return parseFloat(value.toFixed(2));
            }
              const parsed = parseFloat(stringValue);
    
            // If parseFloat consumed the entire string, it's a number
            // Otherwise, it's a stat name like "3FGA" → keep as string
            if (!isNaN(parsed) && String(parsed) === stringValue) {
            if (Number.isInteger(parsed)) {
                return parsed;
            }
            return parseFloat(parsed.toFixed(2));
            }
            
            // Not a pure number, return the original string (stat name)
            return stringValue;
        };


        function handleDropdownChange(key: any, value: any) {
    if (isLoading) return;
    
    const updatedFilters: any = { ...selectedFilters };
    if (key === "seasons" || key === "substat" || key === "stat") {
      updatedFilters[key] = updatedFilters[key].includes(value)
        ? updatedFilters[key].filter((item: any) => item !== value)
        : [...updatedFilters[key], value];
    }

    setSelectedFilters(updatedFilters);
    if (!teamRecordsRes?.TeamRecords.length) return;
  }

  const handleDeleteFilter = (filter: string) => {
    if (isLoading) return;
    
    if (selectedFilters?.seasons.includes(parseInt(filter))) {
      setSelectedFilters({ 
        ...selectedFilters, 
        seasons: selectedFilters.seasons.filter((season) => season !== parseInt(filter)) 
      });
      setCurrentPage(0);
      return;
    }

    const isStatCategory = selectedFilters?.stat?.includes(filter);
    
    if (isStatCategory) {
      const categoryStats = props?.TeamData?.stat_mapping?.[filter] || [];
      const updatedStat = selectedFilters.stat.filter((s: string) => s !== filter);
      const updatedSubstat = selectedFilters.substat.filter(
        (substat: string) => !categoryStats.includes(substat)
      );
      
      setSelectedFilters({
        ...selectedFilters,
        stat: updatedStat,
        substat: updatedSubstat
      });
      setCurrentPage(0);
      return;
    }

    if (selectedFilters?.substat?.includes(filter)) {
      setSelectedFilters({
        ...selectedFilters,
        substat: selectedFilters.substat.filter((s: string) => s !== filter)
      });
      setCurrentPage(0);
      return;
    }
  };


  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(Math.max(0, pageNumber - 1));
  }

  const handleItemsPerPageChange = (_itemsPerPage: number) => {
    setItemsPerPage(_itemsPerPage);
    setCurrentPage(0);
  }

  const handleClearAllFilters = () => {
    if (isLoading) return;
    setSelectedFilters({ seasons: [], substat: [], stat: [] });
  };

  const getTeamRecords = async (duration:"game" | "season" = "game") => {
    try {
      setisLoading(true);
      setErrorMessage("");
      setErrorStatus(0);

      const payload = {
        SportCode: props.TeamData?.SportCode,
        TeamCode: teamCode,
        StatPeriod: duration,
      };

      const teamRecords: { data: TeamsRecordsRes } = await api.post(TEAM_RECORDS, payload);
      console.log("RAW API RESPONSE:", teamRecords.data); // See everything
console.log("stat_mapping in response:", teamRecords.data?.stat_mapping);

      if (teamRecords.data?.TeamRecords) {
        teamRecords.data.TeamRecords = teamRecords.data.TeamRecords.map((teamRecord: any) => {
          const formattedRecord: any = {};
          Object.keys(teamRecord).forEach(key => {
            const value = teamRecord[key];
            if (value === "-" || value === null || value === undefined) {
              formattedRecord[key] = "-";
            } else {
              formattedRecord[key] = formatNumericValue(value);
            }
          });
          return formattedRecord;
        });
      }

      // ✅ CHANGED: Store stat_mapping from API response in local state
      if (teamRecords.data?.stat_mapping) {
        setStatMapping(teamRecords.data.stat_mapping);
        console.log("Updated stat_mapping from API:", teamRecords.data.stat_mapping);
      }

      setTeamRecordsRes(teamRecords.data);
      console.log("team records,", teamRecords);
    } catch (err) {
      console.error("Error fetching team records:", err);
      setErrorMessage("Failed to fetch team records");
      setErrorStatus(500);
    } finally {
      setisLoading(false);
    }
  };

      const getFilteredRowData = () => {
    return ((teamRecordsRes?.TeamRecords ?? []) as any)
      .filter((teamRecord: TeamRecord) => 
        selectedFilters.seasons.length > 0 
          ? selectedFilters.seasons.includes(teamRecord.season) 
          : true
      )
      .filter((teamRecord: TeamRecord) => {
        if (!selectedFilters.stat.length) return true;
        const allStats: string[] = selectedFilters.stat.map((stat: string) => {
          const categoryStats: string[] | undefined = props?.TeamData?.stat_mapping?.[stat];
          return categoryStats ?? [];
        }).flat();
        return allStats.includes(teamRecord.stat);
      })
      .filter((teamHigh: TeamRecord) => {
        if (!selectedFilters.substat.length) return true;
        return selectedFilters.substat.includes(teamHigh.stat)
      });
  }

    const getColumnDefs = () => {
    const allColumns = [
      { field: "season", sortable: true },
      { field: "stat", headerName: "Stat Names", sortable: true },
      { field: "value", headerName: "Value", sortable: true },
      { field: "rank", headerName: "Rank", sortable: true },
      { 
        field: "next_rank_value", 
        headerName: "Next Rank Value",
        cellDataType: 'text',
        sortable: true
      },
      { 
        field: "game_date", 
        headerName: "Game Date", 
        sortable: true,
      },
      { field: "opponent_team_name", headerName: "Opponent Team Name", sortable: true },
    ];

 if (currentDuration === "season") {
      return allColumns.filter(col => 
        !["game_date", "opponent_team_name"].includes(col.field)
      );
    }
    
    return allColumns;
  };


useEffect(() => {
  if (!props.TeamData?.SportCode || !teamCode) return;
  getTeamRecords();
}, [props.TeamData?.SportCode, teamCode]);

  
    useEffect(() => {
      setCurrentPage(0);
    }, [selectedFilters]);
  
    
      const reportIssue = useReportIssue();
    
      const handleReportIssue = (errorContext?: string) => {
        console.log('Report issue clicked!');
        reportIssue.openModal({
          ComponentSource: 'TeamDashboard - Record View',
          ErrorContext: errorContext || errorMessage || '',
          UserContext: {
            sportsCode,
            errorStatus,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
          },
        });
      };


    return(
        <>
      <div className="flex flex-row justify-between items-start">
        <div className="flex flex-row justify-start items-center gap-2 w-full">
          <div className="text-sm font-medium">Filters:-</div>
          <div className="flex gap-2 flex-wrap max-w-[40rem] px-2">
            {[...selectedFilters.stat, ...selectedFilters.seasons, ...selectedFilters.substat].map(
              (filter, index) => (
                <Pill
                  key={index}
                  text={String(filter)}
                  showCross={true}
                  onClick={() => handleDeleteFilter(String(filter))}
                />
              )
            )}
          </div>
          {(selectedFilters.stat.length || selectedFilters.seasons.length || selectedFilters.substat.length) > 0 && (
            <button
              onClick={handleClearAllFilters}
              className="ml-2 text-xs text-red-600 hover:text-red-800 underline whitespace-nowrap"
              disabled={isLoading}
            >
              Clear All
            </button>
          )}
        </div>
        
        <div className="flex flex-row justify-between gap-2 w-full mb-2 min-w-[40rem] self-start">
          <div className={`w-full ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
            <DropdownSelect
              options={["season", "game"]}
              options2={[]}
              def="Select duration"
              setSelectedOption={setCurrentDuration}
              selectedOption={currentDuration}
              onChange={(duration: "season" | "game") => {
                if (!isLoading) {
                  getTeamRecords(duration);
                }
              }}
            />
          </div>
          

            <div className={`w-full ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
              <MultiSelectDropdown
                options={Array.from(
                  new Set(teamRecordsRes?.TeamRecords.map((teamHigh) => teamHigh.season))
                )}
                options2={[]}
                def="Select Season"
                setSelectedOption={handleDropdownChange}
                selectedOption={selectedFilters}
                filterKey="seasons"
                disabled={false}
              />
            </div>

          
          <div className={`w-full ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
            <MultiSelectDropdown
              options={Object.keys(props?.TeamData?.stat_mapping ?? {}) ?? []}
              options2={[]}
              def="Select Category"
              setSelectedOption={handleDropdownChange}
              selectedOption={selectedFilters}
              filterKey="stat"
              disabled={false}
            />
          </div>
          
          <div className={`w-full ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
            <MultiSelectDropdown
              options={
                selectedFilters.stat.map((stat) => props?.TeamData?.stat_mapping?.[stat]).flat()
              }
              options2={[]}
              def="Select Substat"
              setSelectedOption={handleDropdownChange}
              selectedOption={selectedFilters}  
              filterKey="substat"
              disabled={!selectedFilters.stat}
            />
          </div>
        </div>
      </div>

      <div className="w-full overflow-x-auto rounded-lg">
        <div className="ag-theme-alpine" style={{ height: "500px", width: "100%" }}>
        {errorMessage && errorStatus >= 500 ? (
          <div className="bg-red-50 text-red-700 p-4 rounded text-sm mb-4">
            {errorMessage}{" "}
            <button
              className="underline text-blue-600 hover:text-blue-800"
              onClick={() =>
                handleReportIssue(
                  `Failed to fetch team records ( SportCode: ${sportsCode}, HTTP ${errorStatus})`
                )
              }
            >
              Click here to report this issue
            </button>
          </div>
        ) : (
          <>
            <AgGridReact
              rowData={getFilteredRowData()}
              columnDefs={getColumnDefs()}
              defaultColDef={{
                flex: 1,
                sortable: true,
                valueFormatter: (params) => {
                  const val = params.value;
                  return val === null || val === undefined ? "-" : val;
                },
                comparator: (valueA, valueB) => {
                  const isADash = valueA === "-" || valueA === null || valueA === undefined;
                  const isBDash = valueB === "-" || valueB === null || valueB === undefined;
                  
                  if (isADash && isBDash) return 0;
                  if (isADash) return 1;
                  if (isBDash) return -1;
                  
                  // STRICT CHECK: Only parse as number if string is purely numeric (no hyphens)
                  // This prevents "2025-10-11" from being parsed as "2025"
                  const isNumericString = (val: any) => /^-?\d*\.?\d+$/.test(String(val));

                  if (isNumericString(valueA) && isNumericString(valueB)) {
                    return parseFloat(String(valueA)) - parseFloat(String(valueB));
                  }
                  
                  // String comparison for everything else (Dates, Names, mixed text)
                  return String(valueA).localeCompare(String(valueB));
                },
              }}
              pagination={true}
              paginationPageSize={itemsPerPage}
              paginationPageSizeSelector={[10, 25, 50, 100]}
              theme={themeQuartz}
              loadingOverlayComponent={Spinner}
              loading={isLoading}
            />
          </>
        )}
        </div>
      </div>

      <ReportIssueModal
        isOpen={reportIssue.isModalOpen}
        onClose={reportIssue.closeModal}
        onSubmit={reportIssue.handleSubmit}
        issue={reportIssue.issue}
        setIssue={reportIssue.setIssue}
        feedbackMessage={reportIssue.feedbackMessage}
        feedbackType={reportIssue.feedbackType}
        isLoading={reportIssue.isLoading}
      />
    </>
    
    )
};

export default TeamRecordsView;